import { X, ChevronRight, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { categoryLabel } from "../lib/geo";

/**
 * iter129 — Multiple-business selector sheet.
 *
 * Shown when an owner with MORE than one business taps "Business
 * Dashboard" in the profile menu. The single-business case bypasses
 * this sheet and navigates directly to /business/{id}.
 */
export default function BusinessSelectorSheet({ open, onClose, businesses }) {
  const { t } = useI18n();
  const nav = useNavigate();
  if (!open) return null;

  return (
    <div
      data-testid="biz-selector-overlay"
      className="fixed inset-0 z-[80] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-testid="biz-selector-sheet"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md relative"
        style={{
          background: "#171717",
          borderTop: "1px solid rgba(232,184,75,0.3)",
          padding: "10px 0 18px",
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          maxHeight: "84vh",
          overflowY: "auto",
        }}
      >
        <div className="mx-auto" style={{ width: 44, height: 4, background: "rgba(255,255,255,0.18)", borderRadius: 2 }} />

        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="biz-selector-close"
          className="absolute right-3 grid place-items-center"
          style={{ top: 12, width: 36, height: 36, color: "#E8B84B" }}
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>

        <h2
          className="px-5 mt-4"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#F5F1E8" }}
          data-testid="biz-selector-title"
        >
          {t("your_businesses")}
        </h2>

        <ul className="mt-3" data-testid="biz-selector-list">
          {(businesses || []).map((b) => (
            <li key={b.id}>
              <button
                type="button"
                data-testid={`biz-selector-row-${b.id}`}
                onClick={() => { nav(`/business/${b.id}`); onClose?.(); }}
                className="w-full flex items-center gap-3 px-5 transition-colors hover:bg-white/[0.03]"
                style={{ minHeight: 64, borderTop: "1px solid rgba(255,255,255,0.05)" }}
              >
                <span
                  className="grid place-items-center shrink-0"
                  style={{
                    width: 40, height: 40, borderRadius: 6,
                    background: "rgba(232,184,75,0.12)", color: "#E8B84B",
                  }}
                >
                  <Building2 className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <span className="flex-1 text-left min-w-0">
                  <span
                    className="block truncate"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8", fontWeight: 600 }}
                  >
                    {b.name}
                  </span>
                  <span
                    className="block truncate mt-0.5"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3" }}
                  >
                    {[categoryLabel(b.category), b.neighborhood].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <ChevronRight className="w-4 h-4 shrink-0" strokeWidth={1.5} style={{ color: "rgba(255,255,255,0.6)" }} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
